import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useCommonApi } from '../../api';
import { onSuccessCustomApiResponse } from '../../api/utils';
import { Role } from '../types';
import { RegistryIdNameLabel, RegistryType } from '../types/accident';
import { CarBranch, RentalStartType, RentalTakeoverRequest } from '../types/rental';
import { useAuth } from './useAuth';

export const useRentalCar = () => {
  const { user } = useAuth();

  const [rentFinishDialogId, setRentFinishDialogId] = useState<number | null>(null);
  const [rentRemoveDialogId, setRentRemoveDialogId] = useState<number | null>(null);
  const [openStartRentDialogId, setOpenStartRentDialogId] = useState<number | null>(null);

  const { data: carRentalBranches } = useQuery<CarBranch[]>(useCommonApi().getRegistryList(RegistryType.BRANCH_LIST));
  const { data: carRentalStatuses } = useQuery<RegistryIdNameLabel[]>(
    useCommonApi().getRegistryList(RegistryType.CAR_RENTAL_STATUSES)
  );

  const { mutate: removeRent, isPending: isRemovingRent } = useMutation(useCommonApi().removeRent());
  const { mutate: takeoverRent, isPending: isTakingoverRent } = useMutation(useCommonApi().takeoverRent());
  const { mutate: startRent, isPending: isStartingRent } = useMutation(useCommonApi().startRent());
  const { mutate: updateRent, isPending: isUpdatingRent } = useMutation(useCommonApi().updateRent());
  const { mutate: generateDocument, isPending: isGeneratingDocument } = useMutation(
    useCommonApi().regenerateRentalDocument()
  );

  const handleFinishRent = (rentData: RentalTakeoverRequest) => {
    takeoverRent(
      {
        rentId: rentFinishDialogId as number,
        rentData: {
          takeover: {
            ...rentData.takeover,
            type: Role.Dealer,
            userId: Number.parseInt(user?.id ?? '')
          },
          rental: {
            returnDate: rentData.rental.returnDate
          }
        }
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => setRentFinishDialogId(null)
        })
      }
    );
  };

  const handleStartRent = (data: RentalStartType) => {
    startRent(
      {
        rentId: openStartRentDialogId as number,
        data
      },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => setOpenStartRentDialogId(null)
        })
      }
    );
  };

  const handleRemoveRent = (options?: { onSuccess?: () => void }) => {
    removeRent(
      { rentId: rentRemoveDialogId as number },
      {
        onSuccess: onSuccessCustomApiResponse({
          executed: () => {
            setRentRemoveDialogId(null);
            options?.onSuccess?.();
          }
        })
      }
    );
  };

  return {
    rentFinishDialogId,
    setRentFinishDialogId,
    rentRemoveDialogId,
    setRentRemoveDialogId,
    carRentalBranches,
    carRentalStatuses,
    removeRent,
    isRemovingRent,
    takeoverRent,
    handleStartRent,
    openStartRentDialogId,
    isStartingRent,
    setOpenStartRentDialogId,
    isTakingoverRent,
    handleFinishRent,
    handleRemoveRent,
    updateRent,
    isUpdatingRent,
    generateDocument,
    isGeneratingDocument
  };
};
